import type { UIMessage } from 'ai';
import {
  type ToolLoader,
  type ChatData,
  type ChatActionData,
  type Message,
  ChatActionType,
  uiMessageFromPersistedSystemBody,
} from '@cotera/sdk/core';
import type { ToolListItemResponse } from '@cotera/api';
import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from './query-cache-keys';

// ============================================================================
// Chat snapshot row types (from API / conversation snapshot)
// ============================================================================

export type ChatRow = {
  id: string;
  org_id: string;
  /** Org that owns the chat for credential/cross-org purposes. Derived from resource/folder links. */
  owner_org_id?: string;
  actor_id: string;
  agent: string | null;
  title: string;
  status: 'active' | 'archived';
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

export type AgentChatRow = {
  id: string;
  org_id: string;
  agent_id: string;
  chat_id: string;
  created_at: string;
  updated_at: string;
};

export type ChatMessageRow = {
  id: string;
  org_id: string;
  actor_id: string;
  chat_id: string;
  message_id: string | null;
  position: number;
  role: string;
  message: string | Record<string, unknown>;
  usage: Record<string, unknown> | null;
  type: 'chat' | 'compaction' | 'hidden';
  read_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ChatActionRow = {
  id: string;
  org_id: string;
  chat_id: string;
  actor_id: string | null;
  type: ChatActionType;
  status: 'pending' | 'approved' | 'denied' | 'completed' | 'cancelled';
  data: Record<string, unknown> | null;
  completed_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// Transformation Functions (exported for use in CocoChatModel)
// ============================================================================

export function transformChat(row: ChatRow, agentId?: string | null): ChatData {
  return {
    id: row.id,
    orgId: row.org_id,
    ownerOrgId: row.owner_org_id ?? row.org_id,
    actorId: row.actor_id,
    agent: row.agent,
    agentId: agentId ?? null,
    title: row.title,
    status: row.status ?? 'active',
    metadata: row.metadata as { loadedToolIds?: string[] } | null,
  };
}

export function parseUiMessage(
  message: string | Record<string, unknown>
): UIMessage {
  if (typeof message === 'string') {
    return JSON.parse(message) as UIMessage;
  }
  // API returns JSONB as parsed objects
  return message as unknown as UIMessage;
}

export function transformMessages(rows: ChatMessageRow[]): {
  messages: Message[];
  messagePositions: Map<string, number>;
  latestCompactionPosition: number | null;
} {
  const sorted = [...rows].sort((a, b) => a.position - b.position);

  const messages: Message[] = sorted.map((row) => {
    const storedSystemUi = uiMessageFromPersistedSystemBody(
      row.message_id,
      row.message
    );
    const parsed = (storedSystemUi ??
      parseUiMessage(row.message)) as UIMessage & {
      metadata?: unknown;
    };
    const metadata =
      parsed.metadata !== undefined &&
      parsed.metadata !== null &&
      typeof parsed.metadata === 'object'
        ? (parsed.metadata as Record<string, unknown>)
        : {};
    const uiMessageId =
      parsed.id ?? (row.message_id !== null ? row.message_id : undefined);
    const uiMessageRole =
      parsed.role ?? (row.role as UIMessage['role'] | undefined);
    return {
      uiMessage: {
        ...parsed,
        ...(uiMessageId !== undefined ? { id: uiMessageId } : {}),
        ...(uiMessageRole !== undefined ? { role: uiMessageRole } : {}),
        metadata: {
          ...metadata,
          actorId: row.actor_id,
        },
      } as UIMessage,
      type: row.type,
      readAt:
        row.role === 'assistant'
          ? row.read_at !== null
            ? new Date(row.read_at)
            : null
          : undefined,
      createdAt: new Date(row.created_at),
    };
  });

  const messagePositions = new Map<string, number>();
  let latestCompactionPosition: number | null = null;

  for (const row of sorted) {
    if (row.message_id !== null) {
      messagePositions.set(row.message_id, row.position);
    }
    if (row.type === 'compaction') {
      if (
        latestCompactionPosition === null ||
        row.position > latestCompactionPosition
      ) {
        latestCompactionPosition = row.position;
      }
    }
  }

  return { messages, messagePositions, latestCompactionPosition };
}

export function transformActions(rows: ChatActionRow[]): ChatActionData[] {
  return rows
    .map((row) => ({
      id: row.id,
      actorId: row.actor_id,
      chatId: row.chat_id,
      type: row.type,
      status: row.status,
      data: row.data,
      completedAt: row.completed_at ? new Date(row.completed_at) : null,
      approvedAt: row.approved_at ? new Date(row.approved_at) : null,
      approvedBy: row.approved_by,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }))
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

export function createToolLoader(
  queryClient: QueryClient,
  orgId: string
): ToolLoader {
  const getTools = async () => {
    return (
      (await queryClient.getQueryData<ToolListItemResponse[]>(
        queryKeys.toolsV2.all({ orgId })
      )) ?? []
    );
  };
  return {
    loadTools: async (toolIds: string[]) => {
      const tools = await getTools();
      return toolIds
        .map((id) => {
          const tool = tools.find((t) => t.key === id);
          if (tool === undefined) {
            return null;
          }
          return {
            key: tool.key,
            canExecute: () => tool.canExecute,
          };
        })
        .filter((t): t is NonNullable<typeof t> => t !== null);
    },
  };
}
